import React, { useEffect, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useGetCurrentUserQuery, useGetCartQuery } from '../../redux/apis';
import { useGetUnreadCountQuery } from '../../redux/apis/endpoints/messageApi';
import { logout, checkAuth } from '../../redux/slices/authSlice';
import { selectAuthState } from '../../redux/selectors/authSelectors';
import { selectAllCartItems } from '../../redux/slices/cartSlice';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';

// Import new components

import NavItems from './NavItems';
import ActionItems, { CartIcon, MessageIcon } from './ActionItems';
import ProfileSection from './ProfileSection';
import LogoutConfirmModal from './LogoutConfirmModal';

const Nav = () => {
  const [activeItem, setActiveItem] = useState('');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  // Check authentication status on component mount
  useEffect(() => {
    dispatch(checkAuth());
  }, [dispatch]);

  const { isAuthenticated: isLoggedIn, user: userInfo } = useSelector(selectAuthState);

  // Get local cart state
  const allCartItems = useSelector(selectAllCartItems);

  const { data: user } = useGetCurrentUserQuery(undefined, {
    skip: !isLoggedIn,
    refetchOnMountOrArgChange: false,
  });

  // Add cart query to get real-time cart data
  const { data: cartData } = useGetCartQuery(undefined, {
    skip: !isLoggedIn,
  });

  // Get unread message count
  const { data: unreadCount } = useGetUnreadCountQuery(undefined, {
    skip: !isLoggedIn,
  });

  // Get the user display name - prioritize username, fall back to email or 'User'
  const userDisplayName = React.useMemo(() => {
    if (!userInfo) return 'User';
    return userInfo.username || userInfo.email || 'User';
  }, [userInfo]);

  useEffect(() => {
    const menus = [
      { include: 'browse', activeItem: 'Browse' },
      { include: 'about', activeItem: 'About' },
      { include: 'contact', activeItem: 'Contact' },
      { include: 'dashboard', activeItem: 'Dashboard' },
      { include: 'cart', activeItem: 'Cart' },
      { include: 'favorites', activeItem: 'Favorites' },
      { include: 'message', activeItem: 'Messages' },
      { include: 'closet', activeItem: 'SUSU Closet' },
      { include: 'map', activeItem: 'SUSU Map' }
    ];

    const pathname = location.pathname.toLowerCase();
    const match = menus.find((each) => pathname.includes(each.include.toLowerCase()));
    setActiveItem(match ? match.activeItem : '');
  }, [location.pathname]);



  const cartNum = React.useMemo(() => {
    if (!isLoggedIn) return 0;

    // Use the length of the unified allCartItems array
    return allCartItems?.length || 0;

  }, [isLoggedIn, allCartItems]); // Update dependency array

  const handleItemClick = (name, link) => {
    setActiveItem(name);
    setIsMobileMenuOpen(false);
    history.push(link);
  };

  const handleSignOut = () => {
    // First dispatch logout to clear auth state
    dispatch(logout());

    // Then close the modal
    setShowLogoutConfirm(false);

    // Finally redirect to home page
    history.push('/');
  };

  // Navigation items
  const navItems = [
    { name: 'Browse', link: '/browse' },
    { name: 'About', link: '/about' },
    { name: 'Contact', link: '/contact' }
  ];

  // Action items that will be shown in both desktop and mobile menu when logged in
  const actionItems = isLoggedIn ? [
    {
      name: 'Closet',
      icon: null,
      link: '/closet',
      isExternal: false
    },
    // {
    //   name: 'Map',
    //   icon: null,
    //   link: '/map',
    //   isExternal: false
    // },
    {
      name: 'Cart',
      icon: <CartIcon cartCount={cartNum} />,
      link: '/cart'
    },
    {
      name: 'Messages',
      icon: <MessageIcon unreadCount={unreadCount || 0} />,
      link: '/message'
    },
    {
      name: 'Favorites',
      icon: <div className="relative"><svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg></div>,
      link: '/favorites'
    }
  ].filter(Boolean) : [];

  return (
    <>
      <nav style={{ background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(26,26,26,0.08)' }}>
        <div className="max-w-screen-2xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo and main nav */}
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Link to="/" className="font-bold text-xl flex items-center" style={{ fontFamily: "'Space Mono', monospace", fontSize: 15, fontWeight: 700, letterSpacing: 5, color: '#1A1A1A', textDecoration: 'none' }}>
                  SHEYOU
                </Link>
              </div>

              {/* Desktop navigation */}
              <div className="hidden nav:block ml-10">
                <div className="flex items-center space-x-4">
                  <NavItems
                    navItems={navItems}
                    activeItem={activeItem}
                    handleItemClick={handleItemClick}
                  />
                </div>
              </div>
            </div>



            {/* Right side section (Actions + Profile/Login + Hamburger) */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Desktop action items (Cart, Msgs, Favs) */}
              {isLoggedIn && (
                <div className="hidden nav:flex items-center space-x-4">
                  <ActionItems
                    items={actionItems}
                    activeItem={activeItem}
                  />
                </div>
              )}

              {/* Profile/Login (Visible on all screens) */}
              <div className="flex items-center">
                {isLoggedIn ? (
                  <ProfileSection
                    user={user}
                    setShowLogoutConfirm={setShowLogoutConfirm}
                    activeItem={activeItem}
                  />
                ) : (
                  <Link
                    to="/login"
                    className="inline-flex items-center px-6 py-2.5 text-sm font-medium rounded-md text-white hover:text-white bg-brand-orange hover:bg-brand-orange-light transition-colors"
                  >
                    Sign In
                  </Link>
                )}
              </div>

              {/* Mobile menu button */}
              <div className="nav:hidden flex items-center">
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="p-2 rounded-full text-gray-600 hover:bg-gray-100 ml-2"
                  aria-label="Open menu"
                >
                  {isMobileMenuOpen ? (
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile menu dropdown */}
        {isMobileMenuOpen && (
          <div className="nav:hidden" style={{ background: 'rgba(255,255,255,0.98)' }}>
            <div className="px-2 pt-2 pb-3 space-y-1">
              <NavItems
                navItems={navItems}
                activeItem={activeItem}
                handleItemClick={handleItemClick}
                isMobile={true}
              />

              {isLoggedIn && (
                <div className="pt-4 pb-3 border-t" style={{ borderColor: 'rgba(26,26,26,0.08)' }}>
                  <div className="mt-1 px-2 space-y-1">
                    <ActionItems
                      items={actionItems}
                      activeItem={activeItem}
                      isMobile={true}
                    />
                    <Link
                      to="/dashboard/listings"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center w-full px-4 py-2 text-sm rounded-md
                        ${activeItem === 'Dashboard'
                          ? 'text-brand-teal bg-gray-100'
                          : 'text-gray-600 hover:text-brand-teal hover:bg-gray-50'
                        }`}
                    >
                      <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Dashboard
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Logout confirmation modal */}
      <LogoutConfirmModal
        showLogoutConfirm={showLogoutConfirm}
        setShowLogoutConfirm={setShowLogoutConfirm}
        handleSignOut={handleSignOut}
      />
    </>
  );
};

export default Nav; 